import { ReactNode, useState } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { Button } from "@/components/Interface/Button/Button";
import { Checkbox } from "@/components/Interface/FormFields/Checkbox/Checkbox";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import { PasswordField } from "@/components/Interface/FormFields/PasswordField/PasswordField";
import { PasswordStrength } from "@/components/Interface/PasswordStrength/PasswordStrength";
import { FocusRing } from "@/components/Layout/BaseLayouts/Footer/FocusRing";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { VerifyEmailModal } from "@/components/Welcome/VerifyEmailModal";
import { useAlert } from "@/hooks/useAlert";
import { useCustomerLogin } from "@/hooks/useCustomerLogin";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useCreateCustomer } from "@/services/api";
import { CreateCustomerRequest } from "@/services/model";
import { FCC } from "@/types";
import { AlertTypes } from "@/utils/atoms";
import { getLanguageId } from "@/utils/customerUtils";
import { KEYBOARD_TAB_INDEX } from "@/utils/keyboardTabIndex";
import {
  getPasswordRequirementTokens,
  getPasswordValidationError,
} from "@/utils/passwordValidation";
import { createRegisterErrorHandler } from "@/utils/registerErrorHandler";

import { WelcomeCard } from "./WelcomeCard";

interface Props {
  afterRegister?: () => void | Promise<void>;
  handleVerifyEmailModalClose?: () => void;
  /** 2 on pages whose hero headline is already the h1. */
  headingLevel?: 1 | 2;
}

const headingTag = { 1: "h1", 2: "h2" } as const;

// A component, not a plain <a>: Trans replaces an element's children, FocusRing included.
const LegalLink = ({
  href,
  children,
}: {
  href: string;
  children?: ReactNode;
}) => (
  <a
    className="group relative inline w-fit border-b border-solid border-foreground-primary font-semibold text-foreground-primary outline-none hover:-mb-px hover:border-b-2 focus-visible:-mb-px focus-visible:border-b-2"
    href={href}
    tabIndex={KEYBOARD_TAB_INDEX}
    target="_blank"
    rel="noreferrer"
  >
    <FocusRing />
    {children}
  </a>
);

type RegisterFormFields = {
  confirmPassword: string;
  emailAddress: string;
  password: string;
  termsAccepted: boolean;
};

export const RegisterForm: FCC<Props> = ({
  children,
  handleVerifyEmailModalClose,
  afterRegister,
  headingLevel = 1,
}) => {
  const Heading = headingTag[headingLevel];
  const {
    handleSubmit,
    register,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormFields>({
    defaultValues: {
      confirmPassword: "",
      emailAddress: "",
      password: "",
      termsAccepted: false,
    },
    mode: "onChange",
  });

  const { i18n, t } = useTranslation();
  const { shouldShowVerifyEmailModal, setShouldShowVerifyEmailModal } =
    useCustomerLogin();
  const showAlert = useAlert();
  const navigate = useNavigate();
  const [showPasswordStrength, setShowPasswordStrength] =
    useState<boolean>(false);

  const [isAfterRegisterLoginLoading, setIsAfterRegisterLoginLoading] =
    useState(false);
  const registerErrorHandler = createRegisterErrorHandler(
    setError,
    t,
    showAlert,
  );

  const baseLanguage = i18n.language.split("-")[0];

  const { mutate: createCustomer, isPending: isLoading } = useCreateCustomer({
    request: {
      headers: {
        "X-Skip-Auth": "true",
      },
    },
    mutation: {
      onSuccess: () => {
        showAlert({
          text: t(
            "translation:newWelcome.registerForm.uiFeedback.registrationSuccess",
          ),
          type: AlertTypes.success,
        });

        // Start login process after successful registration
        setIsAfterRegisterLoginLoading(true);
        // Show verifyEmailModal to proceed with Login (user will be authorized after finishing email verification)
        setShouldShowVerifyEmailModal(true);
      },
      onError: (error) => {
        registerErrorHandler(error);
      },
    },
  });

  const onSubmit = (data: RegisterFormFields) => {
    if (data.termsAccepted !== true) {
      setError("termsAccepted", {
        type: "manual",
        message: t(
          "translation:newWelcome.registerForm.validation.acceptTerms",
        ),
      });

      return;
    }

    const createCustomerRequest: CreateCustomerRequest = {
      header: {
        client: 1,
        source: 1,
        lastUser: 1,
      },
      basicCustomerData: {
        language: { id: getLanguageId(baseLanguage) },
        email: data.emailAddress,
        mainAddress: {
          country: { id: 200 },
          zip: "0000",
          city: "Dummy",
          street: "Dummy",
        },
      },
      authentification: {
        login: data.emailAddress,
        password: data.password,
      },
    };

    createCustomer({ data: createCustomerRequest });
  };

  const passwordRequirements = getPasswordRequirementTokens(t);

  return (
    <>
      <WelcomeCard>
        <form
          className="flex w-full flex-col gap-6"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Heading className="text-headline-md font-semibold leading-sm text-foreground-primary">
            {t("newWelcome.registerForm.title")}
          </Heading>

          <div className="flex flex-col gap-6">
            <FormField<RegisterFormFields>
              id="emailAddress"
              name="emailAddress"
              label={t("translation:Common.generalForm.fields.email.label")}
              type="email"
              autocomplete="email"
              register={register}
              errors={errors}
              options={{
                required: {
                  value: true,
                  message: t(
                    "translation:Common.generalForm.validation.emailRequired",
                  ),
                },
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: t(
                    "translation:Common.generalForm.validation.emailNotValid",
                  ),
                },
              }}
            />

            <div>
              <PasswordField<RegisterFormFields>
                id="password"
                name="password"
                label={t(
                  "translation:Common.generalForm.fields.password.label",
                )}
                autocomplete="new-password"
                register={register}
                errors={errors}
                options={{
                  required: {
                    value: true,
                    message: t(
                      "translation:Common.generalForm.validation.passwordRequired",
                    ),
                  },
                  validate: (value: string) =>
                    getPasswordValidationError(value, t),
                }}
                onFocus={() => {
                  setShowPasswordStrength(true);
                }}
                onBlur={() => {
                  setShowPasswordStrength(false);
                }}
              />
              {showPasswordStrength && (
                <div className="mt-2">
                  <PasswordStrength
                    password={watch("password")}
                    validationTokens={passwordRequirements}
                    inverted={false}
                  />
                </div>
              )}
            </div>

            <PasswordField<RegisterFormFields>
              id="confirm-password"
              name="confirmPassword"
              label={t(
                "translation:newWelcome.registerForm.confirmPassword.label",
              )}
              autocomplete="new-password"
              register={register}
              errors={errors}
              options={{
                required: {
                  value: true,
                  message: t(
                    "translation:newWelcome.registerForm.validation.confirmPasswordRequired",
                  ),
                },
                validate: (value: string) =>
                  value === watch("password") ||
                  t(
                    "translation:newWelcome.registerForm.validation.passwordNoMatch",
                  ),
              }}
              //! Workaround since onBlur doesn't work on passwordConfirm
              onFocus={() => {
                setShowPasswordStrength(false);
              }}
            />

            <div>
              <Checkbox
                id="termsAccepted"
                name="termsAccepted"
                labelComponent={
                  <span className="block max-w-full text-body-xs leading-md text-foreground-primary">
                    <Trans
                      i18nKey="translation:newWelcome.registerForm.acceptTerms"
                      components={{
                        1: (
                          <LegalLink
                            href={t("translation:Common.links.privacyPolicy")}
                          />
                        ),
                        2: (
                          <LegalLink
                            href={t(
                              "translation:Common.links.terms&Conditions",
                            )}
                          />
                        ),
                      }}
                    />
                  </span>
                }
                register={register}
                options={{
                  required: {
                    value: true,
                    message: t(
                      "translation:newWelcome.registerForm.validation.acceptTerms",
                    ),
                  },
                }}
                errors={errors}
              />

              {shouldShowVerifyEmailModal && (
                <VerifyEmailModal
                  email={watch("emailAddress")}
                  password={watch("password")}
                  handleClose={() => {
                    setShouldShowVerifyEmailModal(false);
                    setIsAfterRegisterLoginLoading(false);

                    if (!!handleVerifyEmailModalClose) {
                      handleVerifyEmailModalClose();
                    }
                  }}
                  handleSuccess={async () => {
                    setShouldShowVerifyEmailModal(false);

                    if (afterRegister) {
                      await afterRegister();
                    }

                    navigate(portalRoutes.personalDataOnboarding, {
                      replace: true,
                    });
                  }}
                />
              )}
            </div>
          </div>

          <div className="flex justify-center">
            <Button
              variant="primary"
              disabled={
                isLoading || isSubmitting || isAfterRegisterLoginLoading
              }
              type="submit"
            >
              {isAfterRegisterLoginLoading ? (
                <LoadingSpinner />
              ) : (
                t("translation:newWelcome.registerForm.title")
              )}
            </Button>
          </div>
        </form>
      </WelcomeCard>

      <div className="flex justify-center py-3">{children}</div>
    </>
  );
};
